import {
  clearStuckSessionCreationMutations,
  reactQueryClient,
} from '@/utils/reactQueryClient';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { OmitKeyof } from '@tanstack/react-query';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';

/**
 * Query keys that are small and critical enough to persist in localStorage.
 * Everything else is excluded to prevent the sync persister from blocking the
 * main thread when deserializing multi MB caches on page load.
 *
 * A persisted query's cached data MUST be JSON-serializable: the sync
 * persister round-trips through JSON.stringify, which silently corrupts
 * Map/Set/Date (`getEntitlements` cached a Map that restored as `{}` and
 * crashed every recording stop — STOP_ERROR_V2 "t.get is not a function").
 */
export const PERSISTED_QUERY_KEYS = new Set([
  'getUser',
  'getUserBillingInfo',
  'getUserCapabilities',
  'getCapabilities',
  'getKindeProperties', // Remove after Kinde to Auth0 migration is complete
  'GetPinnedTemplates',
  'getJoinedTeams',
  'GetSupportedLanguages',
  'getEvidenceConfig',
  'GetPrechartingEnabled',
]);

const getSafePersisterStorage = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

export const persister = createSyncStoragePersister({
  storage: getSafePersisterStorage(),
});

export const persistOptions: OmitKeyof<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  // Bumping the buster discards every previously persisted blob on the next
  // load. 2026-07: old blobs contain a `getEntitlements` entry whose Map data
  // was corrupted to `{}` by JSON serialization; drop them wholesale.
  buster: '2026-07-25-drop-entitlements-map',
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      const firstKey = query.queryKey[0];
      return (
        typeof firstKey === 'string' &&
        PERSISTED_QUERY_KEYS.has(firstKey) &&
        query.state.status === 'success'
      );
    },
  },
};

/**
 * Runs after PersistQueryClientProvider finishes restoring the cache from
 * localStorage. Wired into `onSuccess` of both providers (`_app.tsx` and
 * `minimal-providers.tsx`).
 *
 * Why we invalidate whitelisted queries here:
 * `reactQueryClient` has a global `staleTime` of 1 hour. Without an
 * invalidation pass, a query restored from localStorage is treated as fresh
 * for the full hour, so a page reload skips the network and we serve cached
 * data even when the server-side value changed (e.g. billing or entitlements
 * updated via Internal Admin).
 */
export const onPersistRestored = () => {
  // Drop hung session-creation POSTs before resuming. If we resumed first, a
  // mutation that was `pending` at the previous unload would re-fire instead
  // of being cleared.
  clearStuckSessionCreationMutations();
  void reactQueryClient.resumePausedMutations();
  void reactQueryClient.invalidateQueries({
    predicate: (query) => {
      const firstKey = query.queryKey[0];
      return typeof firstKey === 'string' && PERSISTED_QUERY_KEYS.has(firstKey);
    },
  });
};
